import React, { useEffect, useState } from "react";
import { Coffee, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useLang } from "@/i18n";
import { Spinner } from "@/pages/shared";

// Tijdlijn 00:00–24:00 per medewerker; elke sessie = een balk, kleur volgens het einde (afgemeld / pauze / automatisch / nog open).
const COLOR = { logout: "bg-emerald-500", break: "bg-violet-500", idle: "bg-amber-500", auto: "bg-amber-500", manual: "bg-sky-500", open: "bg-emerald-400 animate-pulse" };
const hhmm = (iso) => new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false });
const pct = (iso, day) => {
  const d = new Date(iso);
  const start = new Date(day + "T00:00:00");
  return Math.min(100, Math.max(0, ((d - start) / 86400000) * 100));
};
const dur = (m) => `${Math.floor(m / 60)}u${String(m % 60).padStart(2, "0")}`;

export default function DayTimeline({ date }) {
  const { t } = useLang();
  const [d, setD] = useState(null);
  useEffect(() => {
    setD(null);
    api.get("/timesheets/day", { params: { date } }).then(({ data }) => setD(data)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  }, [date]);
  if (!d) return <Spinner label={t("loading_data")} />;
  const nowPct = date === new Date().toISOString().slice(0, 10) ? pct(new Date().toISOString(), date) : null;

  return (
    <div className="space-y-3" data-testid="day-timeline">
      <div className="grid grid-cols-[11rem_1fr_4.5rem_4.5rem] items-end gap-3 text-[10px] text-muted-foreground">
        <span />
        <div className="relative h-4">
          {[0, 3, 6, 9, 12, 15, 18, 21, 24].map((h) => (
            <span key={h} className="absolute -translate-x-1/2 font-mono" style={{ left: `${(h / 24) * 100}%` }}>{String(h).padStart(2, "0")}</span>
          ))}
        </div>
        <span className="text-right uppercase tracking-[0.12em]">{t("hours_total")}</span>
        <span className="text-right uppercase tracking-[0.12em]">{t("hours_break")}</span>
      </div>
      {d.people.length === 0 && <p className="py-10 text-center text-sm text-muted-foreground" data-testid="day-timeline-empty">{t("hours_day_none")}</p>}
      {d.people.map((p) => (
        <div key={p.user_id} className={`grid grid-cols-[11rem_1fr_4.5rem_4.5rem] items-center gap-3 rounded-lg ${p.no_break ? "bg-amber-50/70 dark:bg-amber-950/20 ring-1 ring-amber-300/60 px-1 -mx-1" : ""}`} data-testid={`timeline-${p.user_id}`}>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-foreground flex items-center gap-1.5">{p.name}
              {p.no_break && <span className="inline-flex items-center gap-1 rounded-full bg-amber-500 text-white px-1.5 py-0.5 text-[10px] font-bold" title={t("hours_no_break_hint")} data-testid={`no-break-${p.user_id}`}><AlertTriangle className="h-3 w-3" />{t("hours_no_break")}</span>}
            </p>
            <p className="font-mono text-[11px] text-muted-foreground">{p.entries.map((e) => `${hhmm(e.start)}–${e.end ? hhmm(e.end) : "…"}`).join(" · ")}</p>
          </div>
          <div className="relative h-7 rounded-lg bg-muted/70 overflow-hidden">
            {[3, 6, 9, 12, 15, 18, 21].map((h) => <span key={h} className="absolute inset-y-0 w-px bg-border/70" style={{ left: `${(h / 24) * 100}%` }} />)}
            {p.entries.map((e, i) => {
              const l = pct(e.start, date);
              const r = e.end ? pct(e.end, date) : (nowPct ?? 100);
              const key = e.open ? "open" : (e.manual ? "manual" : (e.reason || "logout"));
              return (
                <div key={i} title={`${hhmm(e.start)} – ${e.end ? hhmm(e.end) : t("hours_open")} · ${t(`hours_reason_${key}`)}${e.note ? ` · ${e.note}` : ""}`}
                  className={`absolute inset-y-1 rounded-md ${COLOR[key] || COLOR.logout} shadow-sm`} style={{ left: `${l}%`, width: `${Math.max(0.6, r - l)}%` }} data-testid={`segment-${p.user_id}-${i}`} />
              );
            })}
            {nowPct !== null && <span className="absolute inset-y-0 w-0.5 bg-red-500" style={{ left: `${nowPct}%` }} title={t("view_today")} />}
          </div>
          <span className="text-right font-mono text-sm font-semibold">{dur(p.minutes)}</span>
          <span className={`text-right font-mono text-sm inline-flex items-center justify-end gap-1 ${p.break_minutes ? "text-violet-700" : "text-muted-foreground"}`} title={`${p.breaks}× ${t("hours_reason_break").toLowerCase()}`} data-testid={`break-${p.user_id}`}><Coffee className="h-3.5 w-3.5" />{dur(p.break_minutes)}</span>
        </div>
      ))}
      <div className="flex flex-wrap gap-4 pt-2 text-[11px] text-muted-foreground">
        {["logout", "break", "idle", "manual", "open"].map((k) => (
          <span key={k} className="inline-flex items-center gap-1.5"><span className={`h-2.5 w-4 rounded-sm ${COLOR[k].split(" ")[0]}`} />{t(`hours_reason_${k}`)}</span>
        ))}
      </div>
    </div>
  );
}
